import type { ServiceUsagesGroupReorganizeInput } from '../types/service-usages-group-reorganize-input.type';

// The reorder drawer drags either a group card (among the other groups) or a
// service usage card (within its group or into another group). These two
// kinds never mix: a group can never become a usage nor a usage a group (see
// docs/spec/00-project-spec.md "Feature: Total per group" - "Single reorder
// drawer").
export type ReorderDragDescriptor =
  | Readonly<{ kind: 'group'; groupIndex: number }>
  | Readonly<{ kind: 'usage'; groupIndex: number; usageIndex: number }>;

export type ReorderDropDescriptor =
  | Readonly<{ atIndex: number; kind: 'group' }>
  | Readonly<{ atIndex: number; groupIndex: number; kind: 'usage' }>;

type ReorderGroups = readonly ServiceUsagesGroupReorganizeInput[];

const isValidGroupIndex = (groups: ReorderGroups, index: number): boolean =>
  index >= 0 && index < groups.length;

const moveGroup = (
  groups: ReorderGroups,
  fromIndex: number,
  toIndex: number,
): ReorderGroups => {
  if (!isValidGroupIndex(groups, fromIndex)) {
    return groups;
  }

  const reorderedGroups = [...groups];
  const [movedGroup] = reorderedGroups.splice(fromIndex, 1);

  if (!movedGroup) {
    return groups;
  }

  reorderedGroups.splice(toIndex, 0, movedGroup);

  return reorderedGroups;
};

const moveUsage = ({
  fromGroupIndex,
  fromUsageIndex,
  groups,
  toGroupIndex,
  toUsageIndex,
}: Readonly<{
  fromGroupIndex: number;
  fromUsageIndex: number;
  groups: ReorderGroups;
  toGroupIndex: number;
  toUsageIndex: number;
}>): ReorderGroups => {
  const sourceGroup = groups[fromGroupIndex];
  const targetGroup = groups[toGroupIndex];

  if (!sourceGroup || !targetGroup) {
    return groups;
  }

  const movedUsageId = sourceGroup.serviceUsageIds[fromUsageIndex];

  if (movedUsageId === undefined) {
    return groups;
  }

  const sourceUsageIdsWithoutMoved = sourceGroup.serviceUsageIds.filter(
    (_usageId, index) => index !== fromUsageIndex,
  );

  if (fromGroupIndex === toGroupIndex) {
    const reorderedUsageIds = [...sourceUsageIdsWithoutMoved];
    reorderedUsageIds.splice(toUsageIndex, 0, movedUsageId);

    return groups.map((group, index) =>
      index === fromGroupIndex
        ? { ...group, serviceUsageIds: reorderedUsageIds }
        : group,
    );
  }

  const targetUsageIdsWithMoved = [...targetGroup.serviceUsageIds];
  targetUsageIdsWithMoved.splice(toUsageIndex, 0, movedUsageId);

  return groups.map((group, index) => {
    if (index === fromGroupIndex) {
      return { ...group, serviceUsageIds: sourceUsageIdsWithoutMoved };
    }

    if (index === toGroupIndex) {
      return { ...group, serviceUsageIds: targetUsageIdsWithMoved };
    }

    return group;
  });
};

// Pure state transition for the reorder drawer: given the current ordered
// groups (each with its ordered service usage ids) and a drag/drop pair,
// returns the new ordered groups. A mismatched drag/drop kind (level swap) is
// refused: the groups are returned unchanged.
export const applyReorderDrag = ({
  drag,
  drop,
  groups,
}: Readonly<{
  drag: ReorderDragDescriptor;
  drop: ReorderDropDescriptor;
  groups: ReorderGroups;
}>): ReorderGroups => {
  if (drag.kind === 'group' && drop.kind === 'group') {
    return moveGroup(groups, drag.groupIndex, drop.atIndex);
  }

  if (drag.kind === 'usage' && drop.kind === 'usage') {
    return moveUsage({
      fromGroupIndex: drag.groupIndex,
      fromUsageIndex: drag.usageIndex,
      groups,
      toGroupIndex: drop.groupIndex,
      toUsageIndex: drop.atIndex,
    });
  }

  return groups;
};

// A drop inserts the dragged item at the target position: moving down within
// the same list lands after the target, any other move lands before it.
export const isDropAfterTarget = (
  dragged: ReorderDragDescriptor,
  target: ReorderDragDescriptor,
): boolean => {
  if (dragged.kind === 'group' || target.kind === 'group') {
    return dragged.groupIndex < target.groupIndex;
  }

  return (
    dragged.groupIndex === target.groupIndex &&
    dragged.usageIndex < target.usageIndex
  );
};
